import React, { useState, useEffect } from 'react';
import {
	MoreVertical,
	CircleDollarSign,
	Pencil,
	ClipboardPlus,
	Copy,
	Check,
	Link,
	Users,
	Type,
	RefreshCw,
	Eye,
	Info,
	FileText,
	MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnchoredDropdownMenu } from '@/components/ui/AnchoredDropdownMenu';
import { Tooltip } from '@/components/ui/Tooltip';
import { ColumnDef } from '@/components/data-table/DataTable';
import {
	TransactionReceiptModal,
	Transaction,
} from '@/components/ui/TransactionReceiptModal';
import { SessionService } from '@/services';
import { useReconcileSessionVideoRoom } from '@/services/mutations/session';
import { toast } from 'react-toastify';
import {
	buildPublicSessionAccessUrl,
	formatProfessionalFullName,
	resolveVideoRoomId,
} from '@/utils';
import {
	formatAuditOccurredAtDetail,
	formatAuditOccurredAtParts,
	formatAuditRelativeTime,
} from '@/utils/auditLogDisplay';

const SESSION_TYPE_STYLES: Record<Sessions.SessionType, string> = {
	DEFAULT: 'bg-zinc-100 text-zinc-600',
	RETURN: 'bg-violet-50 text-violet-600',
	GROUP: 'bg-blue-50 text-blue-600',
	P2P: 'bg-zinc-700 text-white',
	P2P_TRANSCRIPTION_ONLY: 'bg-emerald-800 text-white',
};

const CopyIdButton = ({ id }: { id: string }) => {
	const [isCopied, setIsCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigator.clipboard.writeText(id);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={cn(
				'-ml-1.5 flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-tight outline-none transition-all focus:outline-none',
				isCopied
					? 'bg-emerald-50 text-emerald-600'
					: 'text-slate-400 hover:bg-cyan-50 hover:text-cyan-700',
			)}
			title={isCopied ? 'Copiado!' : 'Clique para copiar o ID'}
		>
			{isCopied ? (
				<Check
					size={12}
					strokeWidth={3}
					className="animate-in zoom-in duration-300"
				/>
			) : (
				<Copy size={10} strokeWidth={2.5} />
			)}
			<span>
				{isCopied ? 'COPIADO!' : `#${id?.slice(-5) || '00000'}`}
			</span>
		</button>
	);
};

/** Nome e id do participante principal (compatível com API que retorna user ou participants). */
function getPrimaryParticipant(row: Sessions.Session): {
	name: string;
	userId: string | null;
	patientId: string | null;
} {
	const participant = row.participants?.[0];
	const user = row.user ?? participant?.user;
	const patient = participant?.patient;
	const userId = row.user_id ?? participant?.user_id ?? null;
	const patientId = participant?.patient_id ?? patient?.id ?? null;
	const name =
		patient?.full_name?.trim() ||
		(user
			? `${user.first_name ?? ''} ${(user.last_name ?? '').trim()}`.trim()
			: '') ||
		'—';
	return { name, userId, patientId };
}

const formatSessionScheduledAt = (selectedDate: string): string => {
	const { date, time } = formatAuditOccurredAtParts(selectedDate);
	if (!date) return selectedDate;
	return `${date} ${time.slice(0, 5)}`;
};

const SessionScheduledDateCell = ({
	selectedDate,
}: {
	selectedDate: string;
}) => {
	const relative = formatAuditRelativeTime(selectedDate);
	const scheduledAt = formatSessionScheduledAt(selectedDate);
	const detail = formatAuditOccurredAtDetail(selectedDate);

	const dateTooltip = (
		<div className="space-y-0.5 text-left text-xs leading-relaxed">
			{detail.weekday ? (
				<p className="font-semibold text-zinc-600">{detail.weekday}</p>
			) : null}
			<p className="tabular-nums text-zinc-600">{detail.time}</p>
			<p className="text-zinc-600">{detail.date}</p>
		</div>
	);

	return (
		<div className="flex flex-col gap-0.5">
			<div className="flex items-center gap-1">
				<span className="text-[14px] font-normal tabular-nums text-zinc-700">
					{scheduledAt}
				</span>
				<Tooltip
					content={dateTooltip}
					className="whitespace-normal text-left"
					position="top"
					interactive
				>
					<span className="inline-flex cursor-help text-zinc-400 transition-colors hover:text-zinc-600">
						<Info
							size={12}
							strokeWidth={2.25}
							aria-label="Detalhes da data"
						/>
					</span>
				</Tooltip>
			</div>
			<span className="text-[11px] font-normal text-zinc-700">
				{relative}
			</span>
		</div>
	);
};

const CopyUrlButton = ({ url }: { url: string }) => {
	const [isCopied, setIsCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigator.clipboard.writeText(url);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={cn(
				'flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent px-2 py-1 text-[10px] font-bold uppercase tracking-tight outline-none transition-all focus:outline-none',
				isCopied
					? 'border-emerald-100 bg-emerald-50 text-emerald-600'
					: 'bg-indigo-50 text-indigo-600 hover:border-indigo-200 hover:bg-indigo-100',
			)}
			title={
				isCopied
					? 'Link copiado!'
					: 'Copiar link público de acesso à consulta (sem login)'
			}
		>
			{isCopied ? (
				<Check
					size={12}
					strokeWidth={3}
					className="animate-in zoom-in duration-300"
				/>
			) : (
				<Link size={12} strokeWidth={2.5} />
			)}
			<span>{isCopied ? 'COPIADO!' : 'LINK'}</span>
		</button>
	);
};

const ACTION_ICON_BUTTON_CLASS =
	'flex h-8 w-8 items-center justify-center rounded-xl border border-solid border-stone-300 bg-white text-zinc-600 outline-none transition hover:border-zinc-500 focus:outline-none';

interface ActionMenuProps {
	row: Sessions.Session;
	onView: (data: Sessions.Session) => void;
	onEdit: (data: Sessions.Session) => void;
	onEditDisplayName: (data: Sessions.Session) => void;
	onReturn: (data: Sessions.Session) => void;
	onShowParticipants?: (data: Sessions.Session) => void;
	onViewAudit: (data: Sessions.Session) => void;
	onViewWhatsApp: (data: Sessions.Session) => void;
	onUpdateTransaction: (
		transactionId: string,
		updates: Partial<Transaction>,
		updatedSession?: Sessions.Session,
	) => void;
}

const ActionMenu = ({
	row,
	onView,
	onEdit,
	onEditDisplayName,
	onReturn,
	onShowParticipants,
	onViewAudit,
	onViewWhatsApp,
	onUpdateTransaction,
}: ActionMenuProps) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isReceiptOpen, setIsReceiptOpen] = useState(false);

	const [transactions, setTransactions] = useState<Transaction[]>(
		(row.payments || []) as unknown as Transaction[],
	);

	const [isManualLoading, setIsManualLoading] = useState(false);

	useEffect(() => {
		setTransactions((row.payments || []) as unknown as Transaction[]);
	}, [row.id, row.payments]);

	const handleUpdateTransactionInternal = (
		transactionId: string,
		updates: Partial<Transaction>,
		updatedSession?: Sessions.Session,
	) => {
		// Atualiza lista local de transações
		setTransactions((prev) =>
			prev.map((tx) =>
				tx.id === transactionId ? { ...tx, ...updates } : tx,
			),
		);

		// Propaga para o pai, agora incluindo a sessão atualizada se existir
		onUpdateTransaction(transactionId, updates, updatedSession);
	};

	const handleGenerateManual = async (gateway: Sessions.PaymentGateway) => {
		if (isManualLoading) return;

		try {
			setIsManualLoading(true);

			const response = await SessionService.pay(row.id, gateway);

			console.log('Pagamento Manual Criado com Sucesso:', response);

			setTransactions((prev) => [
				...prev,
				response.payment as Transaction,
			]);
		} catch (error) {
			console.error('Erro ao gerar lançamento manual:', error);
			const message =
				(error as any)?.response?.data?.message ||
				(error as any)?.message ||
				'Ocorreu um erro ao gerar o pagamento manual.';
			toast.error(message);
		} finally {
			setIsManualLoading(false);
		}
	};

	const hasManualTransaction = transactions.some(
		(tx) => tx.gateway === 'MANUAL',
	);

	const disableManualButton = hasManualTransaction;

	const isGroupSession = row.type === 'GROUP';

	const {
		mutate: reconcileVideoRoom,
		isPending: isReconcilingVideoRoom,
		variables: reconcilingSessionId,
	} = useReconcileSessionVideoRoom();
	const isReconcilingThisSession =
		isReconcilingVideoRoom && reconcilingSessionId === row.id;

	const closeMenu = () => setIsMenuOpen(false);

	const handleReconcileVideoRoom = () => {
		if (isReconcilingVideoRoom) return;
		closeMenu();
		reconcileVideoRoom(row.id);
	};

	return (
		<>
			<div className="flex items-center justify-end gap-1.5">
				{row.type === 'P2P' || row.type === 'P2P_TRANSCRIPTION_ONLY' ? (
					<Tooltip content="Mensagens WhatsApp" position="top">
						<button
							type="button"
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								onViewWhatsApp(row);
							}}
							className={ACTION_ICON_BUTTON_CLASS}
							aria-label="Mensagens WhatsApp"
						>
							<MessageCircle size={18} strokeWidth={2.25} />
						</button>
					</Tooltip>
				) : null}
				<Tooltip content="Visualizar sessão" position="top">
					<button
						type="button"
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							onView(row);
						}}
						className={ACTION_ICON_BUTTON_CLASS}
						aria-label="Visualizar sessão"
					>
						<Eye size={18} strokeWidth={2.25} />
					</button>
				</Tooltip>
				<Tooltip content="Auditoria" position="top">
					<button
						type="button"
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							onViewAudit(row);
						}}
						className={ACTION_ICON_BUTTON_CLASS}
						aria-label="Auditoria"
					>
						<FileText size={18} strokeWidth={2.25} />
					</button>
				</Tooltip>
				<AnchoredDropdownMenu
					open={isMenuOpen}
					onOpenChange={setIsMenuOpen}
					trigger={({ ref, isOpen: menuOpen, onTriggerClick }) => (
						<button
							ref={ref}
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								onTriggerClick(event);
							}}
							className={cn(
								ACTION_ICON_BUTTON_CLASS,
								menuOpen &&
									'border-zinc-500 bg-zinc-100 text-zinc-900',
							)}
							aria-label="Mais ações da consulta"
							aria-expanded={menuOpen}
							aria-haspopup="menu"
						>
							<MoreVertical size={18} strokeWidth={3} />
						</button>
					)}
				>
					<div className="flex flex-col py-2">
						{isGroupSession && onShowParticipants && (
							<button
								type="button"
								onClick={() => {
									closeMenu();
									onShowParticipants(row);
								}}
								className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none hover:bg-stone-100"
							>
								<Users size={18} className="text-stone-700" />
								Ver participantes
							</button>
						)}
						{!isGroupSession && (
							<button
								type="button"
								onClick={() => {
									closeMenu();
									setIsReceiptOpen(true);
								}}
								className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none hover:bg-stone-100"
							>
								<CircleDollarSign
									size={18}
									className="text-stone-700"
								/>
								Editar Pagamento
							</button>
						)}
						<button
							type="button"
							onClick={() => {
								closeMenu();
								onEdit(row);
							}}
							className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
						>
							<Pencil size={18} className="text-stone-700" />
							Editar Consulta
						</button>
						<button
							type="button"
							onClick={() => {
								closeMenu();
								onEditDisplayName(row);
							}}
							className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
						>
							<Type size={18} className="text-stone-700" />
							Editar nome da sessão
						</button>
						<button
							type="button"
							onClick={() => {
								closeMenu();
								onReturn(row);
							}}
							className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none hover:bg-stone-100"
						>
							<ClipboardPlus
								size={18}
								className="text-stone-700"
							/>
							Criar Retorno
						</button>
						{row.is_online && (
							<button
								type="button"
								disabled={isReconcilingVideoRoom}
								onClick={handleReconcileVideoRoom}
								className={cn(
									'flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-700 outline-none hover:bg-stone-100',
									isReconcilingVideoRoom &&
										'cursor-not-allowed opacity-50 hover:bg-transparent',
								)}
							>
								<RefreshCw
									size={18}
									className={cn(
										'text-stone-700',
										isReconcilingThisSession &&
											'animate-spin',
									)}
								/>
								{isReconcilingThisSession
									? 'Sincronizando…'
									: 'Sincronizar'}
							</button>
						)}
					</div>
				</AnchoredDropdownMenu>
			</div>

			<TransactionReceiptModal
				isOpen={isReceiptOpen}
				onClose={() => setIsReceiptOpen(false)}
				transactions={transactions}
				patientName={getPrimaryParticipant(row).name}
				sessionId={row.id}
				sessionDate={row.selected_date}
				onManualGenerate={handleGenerateManual}
				onUpdateTransaction={handleUpdateTransactionInternal}
				isManualLoading={isManualLoading}
				disableManualButton={disableManualButton}
			/>
		</>
	);
};

interface SessionColumnsProps {
	onView: (data: Sessions.Session) => void;
	onEdit: (data: Sessions.Session) => void;
	onEditDisplayName: (data: Sessions.Session) => void;
	onReturn: (data: Sessions.Session) => void;
	onShowParticipants?: (data: Sessions.Session) => void;
	onViewAudit: (data: Sessions.Session) => void;
	onViewWhatsApp: (data: Sessions.Session) => void;
	onUpdateTransaction: (
		transactionId: string,
		updates: Partial<Transaction>,
		updatedSession?: Sessions.Session,
	) => void;
}

const SessionColumns = ({
	onView,
	onEdit,
	onEditDisplayName,
	onReturn,
	onShowParticipants,
	onViewAudit,
	onViewWhatsApp,
	onUpdateTransaction,
}: SessionColumnsProps): ColumnDef<
	Sessions.Session,
	keyof Sessions.Session
>[] => {
	return [
		{
			header: 'HORÁRIO / DATA',
			accessor: 'selected_date',
			cell: (value) => (
				<SessionScheduledDateCell selectedDate={value as string} />
			),
		},
		{
			header: 'PROFISSIONAL',
			accessor: 'professional',
			cell: (_: unknown, row: Sessions.Session) => (
				<div className="flex flex-col items-start">
					<span className="max-w-[180px] truncate text-[13px] font-medium text-zinc-600">
						{formatProfessionalFullName(row.professional)}
					</span>
					<CopyIdButton id={row.professional.id} />
				</div>
			),
		},
		{
			header: 'PACIENTE',
			accessor: 'user',
			cell: (_: unknown, row: Sessions.Session) => {
				const isGroup = row.type === 'GROUP';
				const participants = row.participants;
				const count = participants?.length ?? 0;

				if (isGroup) {
					return (
						<div className="flex flex-col items-start">
							<span className="text-[13px] font-medium text-zinc-600">
								Sessão em grupo
							</span>
							<span className="text-[11px] font-medium text-zinc-500">
								{count} participante{count !== 1 ? 's' : ''}
							</span>
						</div>
					);
				}

				const { name, userId, patientId } = getPrimaryParticipant(row);
				const entityId = userId ?? patientId;

				return (
					<div className="flex flex-col items-start">
						<span className="max-w-[180px] truncate text-[13px] font-medium text-zinc-600">
							{name}
						</span>
						{entityId ? <CopyIdButton id={entityId} /> : null}
					</div>
				);
			},
		},
		{
			header: 'TIPO',
			accessor: 'type',
			cell: (_: unknown, row: Sessions.Session) => (
				<span
					className={cn(
						'inline-flex rounded-lg px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wide',
						SESSION_TYPE_STYLES[row.type] ??
							'bg-zinc-100 text-zinc-600',
					)}
				>
					{row.type}
				</span>
			),
		},
		{
			header: 'STATUS',
			accessor: 'status',
			cell: (_value: unknown, row: Sessions.Session) => {
				const statusMap: Record<
					string,
					{ label: string; styles: string }
				> = {
					CREATED: {
						label: 'AGUARDANDO PAGAMENTO',
						styles: 'bg-amber-50 text-amber-700 border-amber-200',
					},
					CONFIRMED: {
						label: 'CONFIRMADO',
						styles: 'bg-emerald-50 text-emerald-700 border-emerald-200',
					},
					STARTED: {
						label: 'EM ANDAMENTO',
						styles: 'bg-orange-50 text-orange-700 border-orange-200',
					},
					FINISHED: {
						label: 'FINALIZADO',
						styles: 'bg-zinc-100 text-zinc-600 border-zinc-200',
					},
					CANCELLED: {
						label: 'CANCELADO',
						styles: 'bg-red-50 text-red-700 border-red-100',
					},
				};

				const paymentsList = row.payments ?? [];
				const hasBenefitApplied =
					Array.isArray(paymentsList) &&
					paymentsList.some((p) => !!p.payment_benefit_application);

				const baseConfig = statusMap[row.status] || {
					label: 'DESCONHECIDO',
					styles: 'bg-gray-50 text-gray-400 border-gray-200',
				};

				const label =
					row.status === 'CONFIRMED' && hasBenefitApplied
						? 'PAGO COM BENEFÍCIO'
						: baseConfig.label;

				return (
					<span
						className={cn(
							'rounded-lg border px-3 py-1 text-[9px] font-black tracking-wider',
							baseConfig.styles,
						)}
					>
						{label}
					</span>
				);
			},
		},
		{
			header: 'LINK',
			accessor: 'video_room',
			cell: (value, row) => {
				const room = value as
					| Sessions.SessionVideoRoom
					| null
					| undefined;
				const roomId = room ? resolveVideoRoomId(room) : null;
				if (!roomId) {
					return (
						<span className="text-[10px] font-medium text-zinc-300">
							—
						</span>
					);
				}

				const { name: participantName } = getPrimaryParticipant(row);
				const userName =
					participantName === '—'
						? undefined
						: participantName.split(/\s+/).filter(Boolean)[0];

				const accessUrl = buildPublicSessionAccessUrl(roomId, userName);

				return <CopyUrlButton url={accessUrl} />;
			},
		},
		{
			header: 'AÇÕES',
			accessor: 'id',
			className: 'text-right',
			cell: (_: unknown, row: Sessions.Session) => (
				<div className="flex justify-end pr-2">
					<ActionMenu
						row={row}
						onView={onView}
						onEdit={onEdit}
						onEditDisplayName={onEditDisplayName}
						onReturn={onReturn}
						onShowParticipants={onShowParticipants}
						onViewAudit={onViewAudit}
						onViewWhatsApp={onViewWhatsApp}
						onUpdateTransaction={onUpdateTransaction}
					/>
				</div>
			),
		},
	];
};

export { SessionColumns };
